import React from 'react'
import { useTranslation } from 'react-i18next'

import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select, { SelectChangeEvent } from '@mui/material/Select'

import { getGraphLimits, getValueSuffix } from '../../utils/graphUtils'
import TimeField from './timeField'
import NumberField from '../fields/numberField'
import { ProfileType } from '../../types/enums/profileType'
import { EndCondition, EndConditionString } from '../../types/enums/endCondition'
import { BasicDataParam } from '../../types/enums/graphDataParam'
import { Machine } from '../../types/machine'
import { getValidEndConditionsForMachine } from '../../utils/profileEditorUtils'
import { SeriesType } from '../../types/enums/seriesType'
import { isPounds } from '../../utils/localStorageUtils'
import ProfileTypeChip from '../profiles/profileTypeChip'
import { Profile } from '../../types/profile'

interface ProfileEditorFormProps {
  isReadonly: boolean
  machine: Machine | null
  editorMaxLength: number
  isBbpProfile: boolean
  children: JSX.Element | Array<JSX.Element>

  isProfileTypeReadonly?: boolean
  profileType: ProfileType
  profileName: string
  setProfileName: (newName: string) => void
  preheatTemp: number
  setPreheatTemp: (newPreheatTemp: number) => void
  batchWeight: number
  setBatchWeight: (newBatchWeight: number) => void
  endCondition: EndCondition
  setEndCondition: (newEndCondition: EndCondition) => void
  endConditionValue: number
  setEndConditionValue: (newEndConditionValue: number) => void
}

const ProfileEditorForm = (props: ProfileEditorFormProps): JSX.Element => {
  const { t } = useTranslation()

  const getEndConditionOptions = (): Array<JSX.Element> => {
    return getValidEndConditionsForMachine(props.machine, props.isBbpProfile).map((x) => (
      <option key={x} value={x}>
        {t(EndConditionString[x])}
      </option>
    ))
  }

  return (
    <form>
      <Stack spacing={2} alignItems="stretch" mt={1}>
        <ProfileTypeChip profile={{ profile_type: props.profileType, is_bbp_profile: props.isBbpProfile } as Profile} />
        <TextField
          value={props.profileName}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => props.setProfileName(event.target.value)}
          label={t('editprofileview.profilename')}
          size="small"
          required
          disabled={props.isReadonly}
          inputProps={{ maxLength: 255 }}
        ></TextField>
        {!props.isBbpProfile && (
          <NumberField
            isSmall
            valueSuffix={getValueSuffix(SeriesType.TEMPERATURE)}
            numValue={props.preheatTemp || ''}
            label={t('editprofileview.preheattemp')}
            disabled={props.isReadonly}
            minNumValue={getGraphLimits(BasicDataParam.ET).min}
            maxNumValue={getGraphLimits(BasicDataParam.ET).max}
            onBlur={(newValue: number) => props.setPreheatTemp(newValue)}
          />
        )}
        {!props.isBbpProfile && (
          <NumberField
            isSmall
            valueSuffix={getValueSuffix(SeriesType.WEIGHT)}
            numValue={props.batchWeight || ''}
            label={t('editprofileview.batchweight')}
            disabled={props.isReadonly}
            minNumValue={0}
            maxNumValue={isPounds() ? 7.05 : 200}
            decimalPlaces={isPounds() ? 2 : undefined}
            onBlur={(newValue: number) => props.setBatchWeight(newValue)}
          />
        )}
        <FormControl disabled={props.isReadonly}>
          <InputLabel htmlFor="end-condition-select">{t('editprofileview.endcondition')}</InputLabel>
          <Select
            native
            id="end-condition-select"
            size="small"
            disabled={props.isReadonly}
            value={props.endCondition.toString()}
            label={t('editprofileview.endcondition')}
            onChange={(event: SelectChangeEvent) => {
              const newEndCondition = parseInt(event.target.value) as EndCondition
              props.setEndCondition(newEndCondition)
            }}
          >
            {getEndConditionOptions()}
          </Select>
        </FormControl>
        {props.endCondition === EndCondition.DevelopmentTimePercentage && (
          <NumberField
            isSmall
            disabled={props.isReadonly}
            valueSuffix="%"
            numValue={props.endConditionValue || ''}
            label={t(EndConditionString[EndCondition.DevelopmentTimePercentage])}
            minNumValue={0}
            maxNumValue={90}
            decimalPlaces={1}
            onBlur={props.setEndConditionValue}
          />
        )}
        {props.endCondition === EndCondition.BeanTemperatureTarget && (
          <NumberField
            isSmall
            disabled={props.isReadonly}
            valueSuffix={getValueSuffix(SeriesType.TEMPERATURE)}
            numValue={props.endConditionValue || ''}
            label={t(EndConditionString[EndCondition.BeanTemperatureTarget])}
            minNumValue={getGraphLimits(BasicDataParam.BT).min}
            maxNumValue={getGraphLimits(BasicDataParam.BT).max}
            decimalPlaces={1}
            onBlur={props.setEndConditionValue}
          />
        )}
        {props.endCondition === EndCondition.DrumTemperatureTarget && (
          <NumberField
            isSmall
            disabled={props.isReadonly}
            valueSuffix={getValueSuffix(SeriesType.TEMPERATURE)}
            numValue={props.endConditionValue || ''}
            label={t(EndConditionString[EndCondition.DrumTemperatureTarget])}
            minNumValue={getGraphLimits(BasicDataParam.BT).min}
            maxNumValue={getGraphLimits(BasicDataParam.BT).max}
            decimalPlaces={1}
            onBlur={props.setEndConditionValue}
          />
        )}
        {props.endCondition === EndCondition.DevelopmentTimeTarget && (
          <TimeField
            disabled={props.isReadonly}
            timeValue={props.endConditionValue}
            minValue={0}
            maxValue={props.editorMaxLength}
            label={t(EndConditionString[EndCondition.DevelopmentTimeTarget])}
            onBlur={props.setEndConditionValue}
          />
        )}
        {props.endCondition === EndCondition.RoastingTimeTarget && (
          <TimeField
            disabled={props.isReadonly}
            timeValue={props.endConditionValue}
            minValue={0}
            maxValue={props.editorMaxLength}
            label={t(EndConditionString[EndCondition.RoastingTimeTarget])}
            onBlur={props.setEndConditionValue}
          />
        )}
        {props.children}
      </Stack>
    </form>
  )
}

export default ProfileEditorForm
